import { Locale, loadLocale } from "i18n";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import localeService from "services/localeService";
import { selectFormLanguage } from "features/qualityForm/qualityFormSlice";
import { useAppSelector } from "app/hooks";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import * as sparkIntl from "genesys-spark";

const LocaleContext = createContext<Locale>("en-US");
export function LocaleProvider({
    children,
    value = "en-US",
}: {
    children: ReactNode;
    value?: Locale;
}) {
    const [locale, setLocale] = useState(value);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadLocaleWrapper = async () => {
            await loadLocale(value);
            setLocale(value);
            setLoading(false);
        };
        loadLocaleWrapper();
    }, [value, locale]);

    return loading ? (
        <></>
    ) : (
        <LocaleContext.Provider value={locale}>
            {children}
        </LocaleContext.Provider>
    );
}
export function AppLocaleProvider({ children }: { children: ReactNode }) {
    const appLocale = localeService.getAppLocale() as Locale;
    return <LocaleProvider value={appLocale}>{children}</LocaleProvider>;
}
export function FormLocaleProvider({ children }: { children: ReactNode }) {
    const formLanguage = useAppSelector((state) => {
        return (
            selectFormLanguage(state) ??
            (localeService.getAppLocale() as Locale)
        );
    });
    return <LocaleProvider value={formLanguage}>{children}</LocaleProvider>;
}
export function useLocale() {
    return useContext(LocaleContext);
}

export function useResolvedLocale() {
    let locale = useLocale();

    const isInternationalizationFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.INTERNATIONALIZATION_DATE_TIME
        );
    });
    if (isInternationalizationFTEnabled) {
        const rootElement = document.querySelector(
            ".quality-forms-app"
        ) as HTMLElement;
        if (rootElement) {
            locale = sparkIntl.Intl.determineDisplayLocale(
                rootElement
            ) as Locale;
        }
    }
    return locale;
}
